import { EventType } from '@ag-ui/core';
import type { InterruptAction } from '../state/reducer';
import type { ChatClient, ChatEvent, ChatRunInput } from './types';

export interface MockTurn {
  /** Assistant reply, streamed token by token. */
  reply?: string;
  /** If set, emits RUN_ERROR with this message instead of a reply. */
  error?: string;
  /** A2UI server_to_client messages, emitted as a CUSTOM "a2ui" event after the text. */
  a2ui?: Record<string, unknown>[];
  /**
   * Deep-agent activity steps to replay before the reply (each a persistent ✓
   * trace). `depth` nests it under the previous shallower step (sub-agent rail).
   * `chunks` streams sub-event deltas into the line's foldable panel — reasoning
   * prose (`kind:'reasoning'`) or tool-arg JSON (`kind:'tool'`).
   *
   * `kind:'delegate'` replays the REAL backend sub-agent wire: a parent `task`
   * tool call (label + streamed delegation args) whose `inner` events arrive as
   * kind-tagged `CUSTOM name:'subagent'` — the delegated agent's steps, streamed
   * answer, and tool calls, surfaced nested under the parent line.
   */
  steps?: {
    kind: 'step' | 'tool' | 'reasoning' | 'delegate';
    detail?: string;
    depth?: number;
    chunks?: string[];
    inner?: {
      kind: 'node' | 'message' | 'tool';
      name?: string;
      status?: 'running' | 'finished';
      delta?: string;
      args?: unknown;
      result?: unknown;
      depth?: number;
    }[];
  }[];
  /**
   * HIL: after the text/steps, pause by finishing with the AG-UI standard
   * `RUN_FINISHED { outcome: { type:'interrupt', interrupts } }`. The demo
   * resumes by re-running with `input.resume` set.
   */
  interrupt?: { id?: string; actions: InterruptAction[] };
  /**
   * show_file (deep-agent local file viewer): emitted as CUSTOM "show_file"
   * events after the reply, each rendered inline as a FileViewer card.
   */
  showFile?: { path: string; title?: string; content: string; uri?: string }[];
}

export interface MockChatClientOptions {
  /** Delay between streamed chunks in ms. Use 0 in tests. */
  delayMs?: number;
  /** Produce the assistant turn for a given input. Defaults to a canned reply. */
  respond?: (input: ChatRunInput) => MockTurn;
}

const DEFAULT_REPLY = '안녕하세요! 이건 백엔드 없이 도는 **Mock** 응답입니다. 무엇을 도와드릴까요? 😊';

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let id: ReturnType<typeof setTimeout>;
    const onAbort = () => {
      clearTimeout(id);
      resolve();
    };
    id = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** In-memory ChatClient that replays a scripted reply. No network, no deps beyond @ag-ui/core. */
export class MockChatClient implements ChatClient {
  constructor(private readonly opts: MockChatClientOptions = {}) {}

  async run(
    input: ChatRunInput,
    onEvent: (event: ChatEvent) => void,
    signal: AbortSignal,
  ): Promise<void> {
    if (signal.aborted) return;
    const delay = this.opts.delayMs ?? 0;
    const turn = this.opts.respond?.(input) ?? { reply: DEFAULT_REPLY };

    onEvent({ type: EventType.RUN_STARTED } as unknown as ChatEvent);

    if (turn.error) {
      onEvent({ type: EventType.RUN_ERROR, message: turn.error } as unknown as ChatEvent);
      return;
    }

    // Deep-agent activity steps (with optional sub-agent nesting) before the reply.
    for (const step of turn.steps ?? []) {
      if (signal.aborted) return;
      if (step.depth) {
        onEvent({ type: EventType.CUSTOM, name: 'subagent', value: { depth: step.depth } } as unknown as ChatEvent);
      }

      if (step.kind === 'reasoning') {
        onEvent({ type: EventType.REASONING_START } as unknown as ChatEvent);
        for (const c of step.chunks ?? []) {
          if (signal.aborted) return;
          await sleep(delay, signal);
          onEvent({ type: EventType.REASONING_MESSAGE_CONTENT, delta: c } as unknown as ChatEvent);
        }
        await sleep(delay, signal);
        if (signal.aborted) return;
        onEvent({ type: EventType.REASONING_END } as unknown as ChatEvent);
      } else if (step.kind === 'tool') {
        onEvent({ type: EventType.TOOL_CALL_START, toolCallName: step.detail } as unknown as ChatEvent);
        for (const c of step.chunks ?? []) {
          if (signal.aborted) return;
          await sleep(delay, signal);
          onEvent({ type: EventType.TOOL_CALL_ARGS, delta: c } as unknown as ChatEvent);
        }
        await sleep(delay, signal);
        if (signal.aborted) return;
        onEvent({ type: EventType.TOOL_CALL_END } as unknown as ChatEvent);
      } else if (step.kind === 'delegate') {
        // Parent `task` tool call (delegation), then the sub-agent's kind-tagged
        // CUSTOM subagent stream (steps / streamed answer / tool calls), then close.
        onEvent({ type: EventType.TOOL_CALL_START, toolCallName: step.detail ?? 'task' } as unknown as ChatEvent);
        for (const c of step.chunks ?? []) {
          if (signal.aborted) return;
          await sleep(delay, signal);
          onEvent({ type: EventType.TOOL_CALL_ARGS, delta: c } as unknown as ChatEvent);
        }
        for (const inner of step.inner ?? []) {
          if (signal.aborted) return;
          await sleep(delay, signal);
          onEvent({
            type: EventType.CUSTOM,
            name: 'subagent',
            value: { ...inner, depth: inner.depth ?? 1 },
          } as unknown as ChatEvent);
        }
        await sleep(delay, signal);
        if (signal.aborted) return;
        onEvent({ type: EventType.TOOL_CALL_END } as unknown as ChatEvent);
      } else {
        onEvent({ type: EventType.STEP_STARTED, stepName: step.detail } as unknown as ChatEvent);
        await sleep(delay, signal);
        if (signal.aborted) return;
        onEvent({ type: EventType.STEP_FINISHED } as unknown as ChatEvent);
      }

      if (step.depth) {
        onEvent({ type: EventType.CUSTOM, name: 'subagent', value: { depth: 0 } } as unknown as ChatEvent);
      }
    }

    onEvent({ type: EventType.TEXT_MESSAGE_START } as unknown as ChatEvent);
    const tokens = (turn.reply ?? '').match(/\S+\s*/g) ?? [];
    for (const tok of tokens) {
      if (signal.aborted) return;
      await sleep(delay, signal);
      if (signal.aborted) return;
      onEvent({ type: EventType.TEXT_MESSAGE_CONTENT, delta: tok } as unknown as ChatEvent);
    }

    // HIL: pause by finishing with the standard interrupt outcome (resume re-runs
    // the turn). One flattened Interrupt per action, id `${lgId}#${index}`.
    if (turn.interrupt) {
      if (signal.aborted) return;
      const lgId = turn.interrupt.id ?? 'int-1';
      const interrupts = turn.interrupt.actions.map((a, i) => ({
        id: `${lgId}#${i}`,
        reason: 'tool_call_review',
        metadata: {
          name: a.name,
          args: a.args,
          allowedDecisions: a.allowedDecisions,
          ...(a.summary ? { summary: a.summary } : {}),
          lgInterruptId: lgId,
          actionIndex: i,
        },
      }));
      onEvent({
        type: EventType.RUN_FINISHED,
        outcome: { type: 'interrupt', interrupts },
      } as unknown as ChatEvent);
      return;
    }

    for (const file of turn.showFile ?? []) {
      if (signal.aborted) return;
      await sleep(delay, signal);
      onEvent({
        type: EventType.CUSTOM,
        name: 'show_file',
        value: { path: file.path, title: file.title, content: file.content, uri: file.uri },
      } as unknown as ChatEvent);
    }

    if (turn.a2ui?.length) {
      if (signal.aborted) return;
      onEvent({
        type: EventType.CUSTOM,
        name: 'a2ui',
        value: { messages: turn.a2ui },
      } as unknown as ChatEvent);
    }

    onEvent({ type: EventType.RUN_FINISHED } as unknown as ChatEvent);
  }
}
